import { Autocomplete, FormControl, FormHelperText, TextField } from '@mui/material';
import { useId } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { z } from 'zod';

type ValueOf<T> = T[keyof T];

export const autoCompleteInputSchema = ((options) => {
	return {
		options,
		create: (fieldName: string, validation: ValueOf<typeof options>) => {
			return z.object({ [fieldName]: validation });
		},
	};
})({
	default: z.string().min(1, 'Required'),
	notRequired: z.string(),
});

export interface IAutoCompleteInputProps {
	name: string;
	label?: string;
	required?: boolean;
	formHelperText?: string;
	dataTestId?: string;
	options: string[];
	disabled?: boolean;
}

export default function AutoCompleteInput({
	name,
	label,
	dataTestId = 'auto_complete',
	formHelperText,
	options = [],
	disabled = false,
}: IAutoCompleteInputProps) {
	const { control } = useFormContext();
	const fieldId = useId();

	const {
		field,
		formState: { errors },
	} = useController({ name, control });

	const error = errors[name];
	const helperMessage = error?.message || formHelperText;

	return (
		<FormControl error={!!error} sx={{ width: '100%' }}>
			<Autocomplete
				id={`auto_complete_${fieldId}`}
				data-test-id={`form_input_${dataTestId}`}
				options={options}
				{...field}
				value={(field.value || null) as string | null}
				onChange={(_, newValue) => field.onChange(newValue || '')}
				renderInput={(params) => (
					<TextField
						error={Boolean(error)}
						label={label}
						{...params}
						required
						sx={{
							'& .MuiInputLabel-root': {
								color: error ? 'error.dark' : undefined,
							},
						}}
					/>
				)}
				disabled={disabled}
			/>
			{helperMessage && (
				<FormHelperText data-test-id={`${dataTestId}_helper_message`}>
					<>{error?.message || formHelperText}</>
				</FormHelperText>
			)}
		</FormControl>
	);
}
